/** Web Push helpers for the CRM portals (standards-based VAPID, no third-party SDK). */

export const pushSupported = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

/** iOS only delivers web push to Home-Screen apps. */
export const needsHomeScreenInstall = () => isIOS() && !isStandalone();

export const permissionState = () => (typeof Notification === "undefined" ? "unsupported" : Notification.permission);

function b64ToBytes(value) {
  const pad = "=".repeat((4 - (value.length % 4)) % 4);
  const raw = atob((value + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch {
    return null;
  }
}

export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/** Must be called from a user gesture. `api` is the authenticated axios instance. */
export async function enablePush(api) {
  if (!pushSupported()) throw new Error("This browser doesn't support notifications.");
  if (needsHomeScreenInstall()) throw new Error("On iPhone, add Breeze to your Home Screen first, then enable notifications from the installed app.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications were blocked. Allow them in your browser or phone settings.");
  const reg = (await registerServiceWorker()) || (await navigator.serviceWorker.ready);
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await api.get("/push/public-key");
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(data.publicKey) });
  }
  await api.post("/push/subscriptions", sub.toJSON());
  return sub;
}

export async function disablePush(api) {
  const sub = await currentSubscription();
  if (!sub) return;
  try { await api.delete("/push/subscriptions", { params: { endpoint: sub.endpoint } }); } catch { /* best effort */ }
  await sub.unsubscribe();
}
